import React, {
     useState,
     createRef,
     useEffect,
     useImperativeHandle,
     forwardRef,
} from "react";
import { useSelector, useDispatch } from "react-redux";
import Cropper, { ReactCropperElement } from "react-cropper";
import "cropperjs/dist/cropper.css";
import Button from "@mui/material/Button";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { styled } from "@mui/material/styles";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import Alert from "@mui/material/Alert";
import { UiButton } from "./UiButton";
import { base64toBinary } from "../../../../_helpers/support.services";
import { imageUploadRequest } from "../../../../_redux/_actions";

const VisuallyHiddenInput = styled("input")({
     clip: "rect(0 0 0 0)",
     clipPath: "inset(50%)",
     height: 1,
     overflow: "hidden",
     position: "absolute",
     bottom: 0,
     left: 0,
     whiteSpace: "nowrap",
     width: 1,
});

export const UiCropFileUpload = forwardRef((props, ref) => {
     const [image, setImage] = useState("");
     const [cropData, setCropData] = useState();
     const [alertShow, setAlertShow] = useState(false);
     const [showImage, setShowImage] = useState(true);
     const cropperRef = createRef();

     const actionDispatch = useDispatch();

     const { aspectRatio, minWidth, label, uploadSize } = props;

     const onChange = (e) => {
          e.preventDefault();
          let files;
          if (e.dataTransfer) {
               files = e.dataTransfer.files;
          } else if (e.target) {
               files = e.target.files;
          }
          const reader = new FileReader();
          reader.onload = () => {
               setImage(reader.result);
               setShowImage(true);
          };
          reader.readAsDataURL(files[0]);
     };

     const getCropData = () => {
          if (typeof cropperRef.current?.cropper !== "undefined") {
               const imageData = cropperRef.current?.cropper
                    .getCroppedCanvas()
                    .toDataURL();
               setCropData(imageData);

               const imageDataBase64 =
                    cropperRef.current?.cropper.getCroppedCanvas();

               if (
                    cropperRef.current?.cropper.getCropBoxData().width <=
                    `${minWidth}`
               ) {
                    setCropData();
                    setAlertShow(true);
                    setTimeout(() => {
                         setAlertShow(false);
                    }, 5000);
               }
          }

          if (cropperRef && cropperRef.current) {
               const cropper = cropperRef.current.cropper;
               if (cropper) {
                    const canvas = cropper.getCroppedCanvas();
                    if (canvas) {
                         try {
                              const base64 = canvas.toDataURL();

                              const timestamp = new Date()
                                   .toISOString()
                                   .replace(/[-:.]/g, "");
                              const randomNum = Math.floor(
                                   Math.random() * 1000000,
                              );

                              const filename = `file_${timestamp}_${randomNum}.png`;
                              const mimeType = "image/png";

                              const file = base64toBinary(
                                   base64,
                                   filename,
                                   mimeType,
                              );

                              actionDispatch(
                                   imageUploadRequest({
                                        file,
                                        setUploadValue: props.setUploadValue,
                                   }),
                              );
                         } catch (error) {
                              console.error(
                                   "Error converting canvas to Base64:",
                                   error,
                              );
                         }
                    } else {
                         console.error("Canvas is null or undefined.");
                    }
               }
          }
     };

     const handleReset = () => {
          setCropData();
          setShowImage(false);
          setImage("");
          props.setUploadValue("");
     };

     // Expose the handleReset function via the ref
     useImperativeHandle(ref, () => ({
          reset: handleReset,
     }));

     useEffect(() => {
          if (props.setToDefault) {
               handleReset();
          }
     }, [props.setToDefault]);

     useEffect(() => {
          if (props.defaultImage) {
               setCropData(props.defaultImage);
          }
     }, [props.defaultImage, props.setToDefault]);

     return (
          <>
               <div className="sba-file-upload-button-wr">
                    <label>{label}</label>
                    <Button
                         component="label"
                         className="sba-upload-button"
                         variant="contained"
                         startIcon={<CloudUploadIcon />}
                    >
                         Upload file
                         <VisuallyHiddenInput
                              type="file"
                              accept="image/*"
                              onChange={onChange}
                         />
                    </Button>
                    <h5>{uploadSize}</h5>
               </div>

               <div className="sba-file-upload-row">
                    {cropData && (
                         <div className="sba-cropped-image">
                              <img src={cropData} />
                              <HighlightOffIcon onClick={handleReset} />
                         </div>
                    )}

                    <div className="sba-file-upload-section">
                         {!cropData && showImage && (
                              <Cropper
                                   ref={cropperRef}
                                   preview=".img-preview"
                                   src={image}
                                   aspectRatio={aspectRatio}
                                   zoomable={false}
                                   background={false}
                                   guides={false}
                              />
                         )}
                    </div>
                    {image && !cropData && (
                         <>
                              <UiButton
                                   variant="contained"
                                   onClick={getCropData}
                                   name="Crop Image"
                              />
                              {alertShow ? (
                                   <Alert severity="error" sx={{ mt: 1 }}>
                                        Cropped Image size is smaller than
                                        minimum size.
                                   </Alert>
                              ) : (
                                   ""
                              )}
                         </>
                    )}
               </div>
          </>
     );
});

export default UiCropFileUpload;
